import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    // Check if user is logged in on mount
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('auth_token');
      if (storedToken) {
        try {
          const response = await fetch(`${backendUrl}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${storedToken}`
            }
          });
          
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setToken(storedToken);
            setIsAuthenticated(true);
          } else {
            // Token invalid, clear storage
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_data');
            setToken(null);
            setUser(null);
            setIsAuthenticated(false);
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_data');
          setToken(null);
          setUser(null);
          setIsAuthenticated(false);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [backendUrl]);

  // Step 1: Login with email/password
  const login = async (email, password) => {
    const response = await fetch(`${backendUrl}/api/auth/email-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Login failed');
    }

    // If MFA is bypassed (DISABLE_MFA env), the API returns a token directly.
    // Persist the session immediately so the user is logged in.
    if (data.token && !data.mfa_required) {
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('user_data', JSON.stringify(data.user));
      setToken(data.token);
      setUser(data.user);
      setIsAuthenticated(true);
    }

    return data;
  };

  // Sign in with password + one-time recovery code (lost authenticator)
  const recoveryLogin = async (email, password, recoveryCode) => {
    const response = await fetch(`${backendUrl}/api/auth/recovery-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, recovery_code: recoveryCode })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'Recovery sign-in failed');
    localStorage.setItem('auth_token', data.token);
    localStorage.setItem('user_data', JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    setIsAuthenticated(true);
    return data;
  };

  // Step 2: Verify MFA code
  const verifyMFA = async (email, code) => {
    const response = await fetch(`${backendUrl}/api/auth/verify-mfa`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code })
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Verification failed');
    }

    // Store token and user data
    localStorage.setItem('auth_token', data.token);
    localStorage.setItem('user_data', JSON.stringify(data.user));
    
    setToken(data.token);
    setUser(data.user);
    setIsAuthenticated(true);

    return data;
  };

  const logout = async () => {
    try {
      if (token) {
        await fetch(`${backendUrl}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const refreshToken = async () => {
    if (!token) return false;
    
    try {
      const response = await fetch(`${backendUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('auth_token', data.token);
        setToken(data.token);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  // Re-fetch the current user from the server and update state (e.g. after a profile photo change).
  const refreshUser = async () => {
    const t = localStorage.getItem('auth_token');
    if (!t) return null;
    try {
      const response = await fetch(`${backendUrl}/api/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
        localStorage.setItem('user_data', JSON.stringify(data));
        return data;
      }
    } catch (error) {
      console.error('refreshUser failed:', error);
    }
    return null;
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    login,
    verifyMFA,
    recoveryLogin,
    logout,
    refreshToken,
    refreshUser,
    setUser,
    isAdmin,
    backendUrl
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
